import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { isClickedOutside } from '@theme/utilities';
import '@theme/sticker-peel';

const STORAGE_KEY = 'tis-peel-to-win';
const DAY = 86_400_000;

/**
 * @typedef {10 | 15 | 20} Tier
 */

/**
 * @typedef {object} PeelRecord
 * @property {'dismissed' | 'revealed' | 'submitting' | 'claimed'} state
 * @property {Tier} [tier]
 * @property {string} [code]
 * @property {boolean} [applied]
 * @property {number} at - When the state last changed.
 */

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {import('@theme/sticker-peel').StickerPeel[]} peels - The stickers to pick from.
 * @property {HTMLElement} sheet - The prize sheet shown with the form and the code.
 * @property {HTMLElement} prizeAmount
 * @property {HTMLElement} rare
 * @property {HTMLElement} status - Live region for the prize announcement.
 * @property {HTMLElement} claimMessage
 * @property {HTMLInputElement} email
 * @property {HTMLInputElement} tags
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} code
 * @property {HTMLButtonElement} copyButton
 * @property {HTMLElement} appliedNote
 * @property {HTMLAnchorElement} applyLink
 * @property {HTMLScriptElement} strings
 * @property {HTMLScriptElement} [codes] - Only rendered right after a successful sign-up.
 * @property {HTMLButtonElement} [launcher]
 * @property {HTMLElement} [launcherLabel]
 */

/**
 * Picks a prize. Each chance is a percentage; everyone else wins 15% off.
 *
 * @param {number} chance20
 * @param {number} chance10
 * @param {() => number} [random]
 * @returns {Tier}
 */
export function drawPrize(chance20, chance10, random = Math.random) {
  const roll = random() * 100;
  if (roll < chance20) return 20;
  if (roll < chance20 + chance10) return 10;
  return 15;
}

/**
 * The "Peel to win" popup. Shoppers peel a sticker to reveal 10%, 15% or 20% off, then sign up to
 * the email list to get the code. The prize and progress are kept in local storage so a refresh
 * can't re-roll the prize, and the claimed code can be shown again from the corner launcher.
 *
 * @extends DialogComponent
 */
export class PeelToWin extends DialogComponent {
  /** @type {AbortController | undefined} */
  #abort;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #openTimer;
  /** @type {PeelRecord | null} Stands in for storage in the theme editor. */
  #memory = null;
  /** @type {Record<string, string>} */
  #strings = {};
  #pressStartedOutside = true;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    try {
      this.#strings = JSON.parse(this.refs.strings?.textContent || '{}');
    } catch {
      this.#strings = {};
    }

    this.addEventListener('sticker-peel:start', this.#handlePeelStart, { signal });
    this.addEventListener('sticker-peel:complete', this.#handlePeelComplete, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose, { signal });
    // The popup opens by itself, so start focus on the card rather than ringing the close button
    this.addEventListener(DialogOpenEvent.eventName, () => this.refs.dialog.focus({ preventScroll: true }), { signal });
    this.addEventListener('submit', this.#handleSubmit, { signal });
    this.addEventListener('pointerdown', this.#trackPress, { capture: true, signal });
    this.addEventListener('click', this.#guardOutsideClick, { capture: true, signal });

    if (this.#inEditor) {
      document.addEventListener('shopify:section:select', this.#handleEditorSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#handleEditorDeselect, { signal });
      return;
    }

    this.#restore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    clearTimeout(this.#openTimer);
  }

  /**
   * Opens the popup from the corner launcher.
   */
  openFromLauncher() {
    this.#open();
  }

  /**
   * Peels one of the stickers automatically.
   */
  peelForMe() {
    const available = this.#peels.filter((peel) => !peel.inert);
    available[Math.floor(Math.random() * available.length)]?.peel();
  }

  /**
   * Copies the claimed code.
   */
  async copyCode() {
    const { code, copyButton } = this.refs;
    const text = code.textContent?.trim() ?? '';

    try {
      await navigator.clipboard.writeText(text);
      copyButton.textContent = this.#strings.copied ?? '';
      setTimeout(() => (copyButton.textContent = this.#strings.copy_code ?? ''), 2000);
    } catch {
      // Clipboard blocked: select the code so it can be copied by hand
      const range = document.createRange();
      range.selectNodeContents(code);
      window.getSelection()?.removeAllRanges();
      window.getSelection()?.addRange(range);
    }
  }

  get #inEditor() {
    return Boolean(window.Shopify?.designMode);
  }

  get #peels() {
    return this.refs.peels ?? [];
  }

  /** @param {PointerEvent} event */
  #trackPress = (event) => {
    this.#pressStartedOutside = isClickedOutside(event, this.refs.dialog);
  };

  /**
   * DialogComponent closes the card on any click outside it. Only let a deliberate tap on the
   * backdrop through: one that also started outside the card, before any sticker was peeled. A peel
   * that ends past the card edge, or a tap on the sticker as it flies off, must not close it.
   *
   * @param {MouseEvent} event
   */
  #guardOutsideClick = (event) => {
    const { dialog } = this.refs;
    if (!dialog.open || !isClickedOutside(event, dialog)) return;

    const peeling = this.#peels.some((peel) => peel.isPeeling);
    if (!this.#pressStartedOutside || peeling || this.#read()?.tier) event.stopPropagation();
  };

  /**
   * Picks up where the shopper left off and decides whether to open by itself.
   */
  #restore() {
    let record = this.#read();
    const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';

    if (record?.state === 'submitting' && record.tier) {
      if (posted) {
        this.#claim(record.tier);
        return;
      }

      record = { ...record, state: 'revealed' };
      this.#write(record);

      if (this.querySelector('.peel-to-win__error')) {
        this.#open();
        return;
      }
    }

    const reshowAfter = Number(this.dataset.reshowDays || 14) * DAY;
    const stale = record && Date.now() - record.at > reshowAfter;

    if (record?.state === 'claimed' && stale) {
      this.#write(null);
      record = null;
    } else if (record?.state === 'dismissed' && stale) {
      record = null;
    }

    this.#updateLauncher();

    if (!record || (record.state === 'revealed' && stale)) {
      this.#scheduleOpen(Number(this.dataset.delay || 0) * 1000);
    }
  }

  /** @param {number} delay */
  #scheduleOpen(delay) {
    clearTimeout(this.#openTimer);
    this.#openTimer = setTimeout(() => {
      // Wait for any other dialog, such as the cart drawer, to close first
      if (document.querySelector('dialog[open]')) {
        this.#scheduleOpen(4000);
        return;
      }
      this.#open();
    }, delay);
  }

  #open() {
    clearTimeout(this.#openTimer);
    const record = this.#read();

    if (record?.state === 'claimed') {
      this.#showClaimed(record);
    } else if (record?.tier) {
      this.#showRevealed(record.tier);
    } else {
      this.#showIntro();
    }

    if (this.refs.launcher) this.refs.launcher.hidden = true;
    this.showDialog();
  }

  #showIntro() {
    for (const peel of this.#peels) {
      peel.inert = false;
      peel.parentElement?.classList.remove('is-dimmed');
      const prize = peel.parentElement?.querySelector('[data-slot-prize]');
      if (prize) prize.textContent = '';
      peel.reset();
    }
    this.refs.dialog.dataset.view = 'intro';
  }

  /** @param {Tier} tier */
  #showRevealed(tier) {
    const { dialog, claimMessage, submit } = this.refs;

    this.#fillPrize(tier);
    claimMessage.textContent = this.#text('claim_message', tier);
    submit.textContent = this.#text('get_code', tier);
    dialog.dataset.view = 'revealed';
  }

  /** @param {PeelRecord} record */
  #showClaimed(record) {
    const { dialog, code, appliedNote, applyLink } = this.refs;
    if (!record.tier) return;

    this.#fillPrize(record.tier);
    code.textContent = record.code ?? '';
    appliedNote.hidden = !record.applied;
    applyLink.hidden = Boolean(record.applied);
    applyLink.href = this.#discountUrl(record.code ?? '', window.location.pathname);
    dialog.dataset.view = 'claimed';
  }

  /** @param {Tier} tier */
  #fillPrize(tier) {
    this.refs.prizeAmount.textContent = this.#text('prize_amount', tier);
    this.refs.rare.hidden = tier !== 20;
  }

  /** @param {Event} event */
  #handlePeelStart = (event) => {
    const chosen = event.target;
    let tier = this.#read()?.tier;

    if (!tier) {
      tier = drawPrize(Number(this.dataset.chance20 || 0), Number(this.dataset.chance10 || 0));
      this.#write({ state: 'revealed', tier, at: Date.now() });
    }

    this.#fillPrize(tier);

    // Print the prize under the chosen sticker and set the other two aside
    for (const peel of this.#peels) {
      const slot = peel.parentElement;
      if (peel === chosen) {
        const prize = slot?.querySelector('[data-slot-prize]');
        if (prize) prize.textContent = this.#text('prize_amount', tier);
      } else {
        peel.inert = true;
        slot?.classList.add('is-dimmed');
      }
    }
  };

  #handlePeelComplete = () => {
    const tier = this.#read()?.tier;
    if (!tier) return;

    const { dialog, status, email } = this.refs;
    const focusWasInside = dialog.contains(document.activeElement) && document.activeElement !== dialog;

    this.#showRevealed(tier);
    status.textContent = this.#text('won', tier);

    // Keyboard users lose their place when the sticker goes; move them to the form
    if (focusWasInside && document.activeElement instanceof HTMLElement && !document.activeElement.offsetParent) {
      email.focus({ preventScroll: true });
    }
  };

  /**
   * Records the prize on the form just before it posts. Shopify reloads the page afterwards.
   *
   * @param {SubmitEvent} event
   */
  #handleSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.contains(this.refs.tags)) return;

    const record = this.#read();
    if (!record?.tier) return;

    this.refs.tags.value = `newsletter,peel-to-win,peel-${record.tier}`;
    this.#write({ ...record, state: 'submitting', at: Date.now() });
  };

  #handleClose = () => {
    const record = this.#read();

    if (!record) {
      this.#write({ state: 'dismissed', at: Date.now() });
    } else if (record.state === 'revealed') {
      this.#write({ ...record, at: Date.now() });
    }

    this.#updateLauncher();
  };

  /** @param {Event} event */
  #handleEditorSelect = (event) => {
    if (!(event instanceof CustomEvent) || event.detail?.sectionId !== this.dataset.sectionId) return;
    this.#memory = null;
    this.#open();
  };

  /** @param {Event} event */
  #handleEditorDeselect = (event) => {
    if (!(event instanceof CustomEvent) || event.detail?.sectionId !== this.dataset.sectionId) return;
    this.closeDialog();
  };

  /**
   * Shows the code after Shopify confirms the sign-up, and applies it to the cart session.
   *
   * @param {Tier} tier
   */
  async #claim(tier) {
    /** @type {Record<string, string>} */
    let codes = {};
    try {
      codes = JSON.parse(this.refs.codes?.textContent || '{}');
    } catch {
      codes = {};
    }

    const code = codes[String(tier)] ?? '';
    /** @type {PeelRecord} */
    const record = { state: 'claimed', tier, code, at: Date.now() };

    this.#write(record);
    this.#showClaimed(record);
    if (!code) this.refs.code.textContent = this.#strings.code_missing ?? '';
    this.showDialog();

    if (!code) return;

    try {
      // Shopify answers with a redirect and sets the discount cookie; there is no need to follow it
      const response = await fetch(this.#discountUrl(code), { credentials: 'same-origin', redirect: 'manual' });
      if (!response.ok && response.type !== 'opaqueredirect') {
        throw new Error(`Discount link returned ${response.status}`);
      }

      record.applied = true;
      this.#write(record);
      this.refs.appliedNote.hidden = false;
      this.refs.applyLink.hidden = true;
    } catch {
      // The "Apply code and shop" link stays visible as the fallback
    }
  }

  #updateLauncher() {
    const { launcher, launcherLabel } = this.refs;
    if (!launcher || !launcherLabel) return;

    const record = this.#read();
    if (!record || this.refs.dialog.open) {
      launcher.hidden = true;
      return;
    }

    if (record.state === 'claimed' && record.tier) {
      launcherLabel.textContent = this.#text('launcher_code', record.tier);
    } else if (record.tier) {
      launcherLabel.textContent = this.#text('launcher_claim', record.tier);
    } else {
      launcherLabel.textContent = this.#strings.launcher ?? '';
    }

    launcher.hidden = false;
  }

  /**
   * @param {string} code
   * @param {string} [redirect]
   */
  #discountUrl(code, redirect) {
    const root = window.Shopify?.routes?.root ?? '/';
    const url = new URL(`${root}discount/${encodeURIComponent(code)}`, window.location.origin);
    if (redirect) url.searchParams.set('redirect', redirect);
    return url.toString();
  }

  /**
   * @param {string} key
   * @param {Tier} tier
   */
  #text(key, tier) {
    return (this.#strings[key] ?? '').replace('[amount]', String(tier));
  }

  /** @returns {PeelRecord | null} */
  #read() {
    if (this.#inEditor) return this.#memory;

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return this.#memory;
    }
  }

  /** @param {PeelRecord | null} record */
  #write(record) {
    this.#memory = record;
    if (this.#inEditor) return;

    try {
      if (record) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Storage can be unavailable in private browsing; the in-memory copy covers this page view
    }
  }
}

if (!customElements.get('peel-to-win')) customElements.define('peel-to-win', PeelToWin);
